import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ElementRef,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { MIME_TYPES } from '../../constants';

export type FileUploadType = 'image' | 'document' | 'excel' | 'pdf' | 'video' | 'audio' | 'custom';

export interface FileUploadConfig {
  acceptedFileTypes?: string[];
  acceptAttribute?: string;
  maxFileSize?: number; // in MB
  showPreview?: boolean;
  selectButtonText?: string;
  changeButtonText?: string;
  removeButtonText?: string;
  placeholderText?: string;
  showValidationError?: boolean;
  customErrorMessage?: string;
  isShowFullWidth?: boolean;
  maxWidth?: number; // in pixels
  maxHeight?: number; // in pixels
  validateDimensions?: boolean;
}

export interface FileUploadResult {
  fileName: string | null;
  fileBase64: string | null;
  fileUrl: string | null;
  file: File | null;
  isValid: boolean;
}

@Component({
  selector: 'piqet-angular-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  standalone: false,
})
export class FileUploadComponent implements OnInit, OnChanges {
  @ViewChild('hiddenfileinput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;

  // Inputs
  @Input() type: FileUploadType = 'image';
  @Input() customConfig: Partial<FileUploadConfig> = {}; // For overriding specific properties
  @Input() existingFileUrl: string | null = null;
  @Input() existingFileName: string | null = null;
  @Input() isDisabled = false;
  @Input() customClass = '';
  @Input() isImageValid = true;

  // Outputs
  @Output() fileSelected = new EventEmitter<FileUploadResult>();
  @Output() fileRemoved = new EventEmitter<void>();
  @Output() removeConfirmation = new EventEmitter<void>();

  // Component state
  selectedFiles: string | null = null;
  imageUrl: string | null = null;
  fileBase64: string | null = null;
  isShowChange = false;
  currentFile: File | null = null;
  internalImageValid = true;
  dynamicErrorMessage = '';
  isProcessingFile = false;
  lastInvalidFileName = '';

  // Predefined configurations for different file types
  private readonly typeConfigurations: Record<FileUploadType, FileUploadConfig> = {
    image: {
      acceptedFileTypes: ['image/png', 'image/jpg', 'image/jpeg'],
      acceptAttribute: '.png,.jpg,.jpeg',
      maxFileSize: 5,
      showPreview: true,
      selectButtonText: 'Select image',
      changeButtonText: 'Change',
      removeButtonText: 'Remove',
      placeholderText: 'Upload image',
      showValidationError: true,
      validateDimensions: false,
      customErrorMessage: 'Only PNG, JPG and JPEG images are allowed',
    },
    document: {
      acceptedFileTypes: [
        MIME_TYPES.APPLICATION_PDF,
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
      ],
      acceptAttribute: '.pdf,.doc,.docx,.txt',
      maxFileSize: 10,
      showPreview: false,
      selectButtonText: 'Select document',
      changeButtonText: 'Change',
      removeButtonText: 'Remove',
      placeholderText: 'Upload document',
      showValidationError: true,
      customErrorMessage: 'Only PDF, DOC, DOCX, and TXT documents are allowed (max 10MB)',
    },
    excel: {
      acceptedFileTypes: [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv',
      ],
      acceptAttribute: '.xls,.xlsx,.csv',
      maxFileSize: 15,
      showPreview: false,
      selectButtonText: 'Select spreadsheet',
      changeButtonText: 'Change',
      removeButtonText: 'Remove',
      placeholderText: 'Upload spreadsheet',
      showValidationError: true,
      customErrorMessage: 'Only XLS, XLSX, and CSV files are allowed (max 15MB)',
    },
    pdf: {
      acceptedFileTypes: [MIME_TYPES.APPLICATION_PDF],
      acceptAttribute: '.pdf',
      maxFileSize: 20,
      showPreview: false,
      selectButtonText: 'Select PDF',
      changeButtonText: 'Change',
      removeButtonText: 'Remove',
      placeholderText: 'Upload PDF',
      showValidationError: true,
      customErrorMessage: 'Only PDF files are allowed (max 20MB)',
    },
    video: {
      acceptedFileTypes: ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/webm'],
      acceptAttribute: '.mp4,.mpeg,.mov,.webm',
      maxFileSize: 100,
      showPreview: false,
      selectButtonText: 'Select video',
      changeButtonText: 'Change',
      removeButtonText: 'Remove',
      placeholderText: 'Upload video',
      showValidationError: true,
      customErrorMessage: 'Only MP4, MPEG, MOV, and WEBM video files are allowed (max 100MB)',
    },
    audio: {
      acceptedFileTypes: ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/ogg'],
      acceptAttribute: '.mp3,.wav,.ogg,.mpeg',
      maxFileSize: 50,
      showPreview: false,
      selectButtonText: 'Select audio',
      changeButtonText: 'Change',
      removeButtonText: 'Remove',
      placeholderText: 'Upload audio',
      showValidationError: true,
      customErrorMessage: 'Only MP3, WAV, and OGG audio files are allowed (max 50MB)',
    },
    custom: {
      acceptedFileTypes: ['*/*'],
      acceptAttribute: '*',
      maxFileSize: 10,
      showPreview: false,
      selectButtonText: 'Select file',
      changeButtonText: 'Change',
      removeButtonText: 'Remove',
      placeholderText: 'Upload file',
      showValidationError: true,
      customErrorMessage: 'Invalid file',
    },
  };

  // Default configuration (fallback)
  private defaultConfig: FileUploadConfig = {
    acceptedFileTypes: ['*/*'],
    acceptAttribute: '*',
    maxFileSize: 5,
    showPreview: false,
    selectButtonText: 'Select file',
    changeButtonText: 'Change',
    removeButtonText: 'Remove',
    placeholderText: 'Upload file',
    showValidationError: true,
    customErrorMessage: 'Invalid file',
    isShowFullWidth: false,
  };

  // Merged configuration
  mergedConfig: FileUploadConfig = {};
  dimensionError = false;

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['existingFileUrl'] || changes['existingFileName']) {
      this.updateExistingFile();
    }
    if (changes['type'] || changes['customConfig']) {
      this.updateConfiguration();
    }
    if (changes['isImageValid']) {
      this.internalImageValid = this.isImageValid;
    }
  }

  private initializeComponent(): void {
    this.updateConfiguration();
    this.updateExistingFile();
  }

  private updateConfiguration(): void {
    // Get the base configuration for the selected type
    const baseConfig = this.typeConfigurations[this.type] || this.defaultConfig;

    // Merge with any custom overrides from parent
    this.mergedConfig = { ...baseConfig, ...this.customConfig };
  }

  private updateExistingFile(): void {
    if (this.existingFileUrl) {
      this.imageUrl = this.existingFileUrl;
      this.isShowChange = true;

      if (this.existingFileName) {
        this.selectedFiles = this.existingFileName;
      } else {
        // Extract filename from URL if not provided
        const urlParts = this.existingFileUrl.split('/');
        this.selectedFiles = urlParts[urlParts.length - 1] || null;
      }
    }
  }

  fileChangeEvent(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      this.resetFileState();
      return;
    }

    this.isProcessingFile = true;

    // Validate file type
    const isValidType = this.validateFileType(file);
    if (!isValidType) {
      this.internalImageValid = false;
      this.lastInvalidFileName = file.name;

      // Create a more specific error message
      const allowedExtensions = this.getAllowedExtensions();
      this.dynamicErrorMessage = `"${file.name}" is not allowed. Please select ${allowedExtensions} files only.`;

      input.value = '';
      this.isProcessingFile = false;
      return;
    }

    // Validate file size
    const isValidSize = this.validateFileSize(file);
    if (!isValidSize) {
      this.internalImageValid = false;
      this.lastInvalidFileName = file.name;
      this.dynamicErrorMessage = `"${file.name}" exceeds ${this.mergedConfig.maxFileSize}MB size limit`;

      input.value = '';
      this.isProcessingFile = false;
      return;
    }

    // File is valid - clear any previous error
    this.internalImageValid = true;
    this.dynamicErrorMessage = '';
    this.lastInvalidFileName = '';
    this.isProcessingFile = false;
    this.processFile(file);
  }

  private validateFileType(file: File): boolean {
    const validTypes = this.mergedConfig.acceptedFileTypes || [];

    // If accept all files
    if (validTypes.includes('*/*')) {
      return true;
    }

    return validTypes.some(type => {
      if (type.includes('*')) {
        const baseType = type.split('/')[0];
        return file.type.startsWith(baseType);
      }
      return file.type === type;
    });
  }

  private validateFileSize(file: File): boolean {
    const maxSize = (this.mergedConfig.maxFileSize || 5) * 1024 * 1024; // Convert MB to bytes
    return file.size <= maxSize;
  }

  private processFile(file: File): void {
    this.currentFile = file;
    this.selectedFiles = file.name;
    this.isShowChange = true;

    // Clear any previous error message on successful file selection
    this.dynamicErrorMessage = '';
    this.internalImageValid = true;
    this.dimensionError = false;

    // Read file for preview and base64
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const result = e.target?.result as string;

      // Check dimensions for image files if validation is enabled
      if (file.type.startsWith('image/') && this.mergedConfig.validateDimensions) {
        const img = new Image();
        img.onload = () => {
          const width = img.width;
          const height = img.height;
          const maxWidth = this.mergedConfig.maxWidth ?? Infinity;
          const maxHeight = this.mergedConfig.maxHeight ?? Infinity;

          if (width > maxWidth || height > maxHeight) {
            this.dimensionError = true;
            this.internalImageValid = false;
            this.dynamicErrorMessage = `Image dimensions (${width}x${height}) exceed maximum allowed size (${maxWidth}x${maxHeight})`;
            // Clear the file selection
            this.fileBase64 = null;
            this.imageUrl = null;
          } else {
            this.dimensionError = false;
            this.internalImageValid = true;
            this.dynamicErrorMessage = '';

            // Set image URL for preview display and for emitting to parent
            if (this.mergedConfig.showPreview) {
              this.imageUrl = result;
            }

            this.fileBase64 = result.split(',')[1];
            this.emitFileSelection(result);
          }
        };

        img.onerror = () => {
          this.internalImageValid = false;
          this.dynamicErrorMessage = 'Failed to load image for validation';
        };

        img.src = result;
      } else {
        // Non-image file or no dimension validation needed
        if (this.mergedConfig.showPreview && file.type.startsWith('image/')) {
          this.imageUrl = result;
        }

        this.fileBase64 = result.split(',')[1];
        this.emitFileSelection(result);
      }
    };
    reader.readAsDataURL(file);
  }

  private emitFileSelection(fileDataUrl?: string): void {
    const result: FileUploadResult = {
      fileName: this.selectedFiles,
      fileBase64: this.fileBase64,
      fileUrl: this.imageUrl || fileDataUrl || null,
      file: this.currentFile,
      isValid: this.internalImageValid,
    };
    this.fileSelected.emit(result);
  }

  private getAllowedExtensions(): string {
    const acceptAttribute = this.mergedConfig.acceptAttribute ?? '';

    if (!acceptAttribute || acceptAttribute === '*') {
      return 'the specified type of';
    }
    return acceptAttribute.split(',').join(', ');
  }

  onSelectClick(): void {
    if (!this.isDisabled) {
      this.fileInput.nativeElement.click();
    }
  }

  onChangeClick(): void {
    if (!this.isDisabled) {
      this.fileInput.nativeElement.click();
    }
  }

  onRemoveClick(): void {
    if (!this.isDisabled) {
      this.removeConfirmation.emit();
    }
  }

  onCrossClick(): void {
    if (!this.isDisabled) {
      // Reset the component state immediately
      this.resetFileState();
      // Emit the removal event so parent can handle confirmation
      this.removeConfirmation.emit();
    }
  }

  // Public method to reset the component
  public reset(): void {
    this.resetFileState();
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
    this.fileRemoved.emit();
  }

  // Public method to clear the file
  public clearFile(): void {
    this.resetFileState();
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  private resetFileState(): void {
    this.selectedFiles = null;
    this.imageUrl = null;
    this.fileBase64 = null;
    this.isShowChange = false;
    this.currentFile = null;
    this.internalImageValid = true;
    this.dynamicErrorMessage = '';
    this.lastInvalidFileName = '';
    this.isProcessingFile = false;
    this.dimensionError = false;
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  // Getters for template
  get showImage(): boolean {
    return !!(this.mergedConfig.showPreview && (this.imageUrl || this.existingFileUrl));
  }

  get displayImageUrl(): string | null {
    return this.imageUrl || this.existingFileUrl;
  }

  get displayFileName(): string | null {
    return this.selectedFiles || this.existingFileName;
  }

  get showError(): boolean {
    return (
      (!this.internalImageValid || !!this.dynamicErrorMessage || this.dimensionError) &&
      (this.mergedConfig.showValidationError ?? true)
    );
  }

  get errorMessage(): string {
    return (
      this.dynamicErrorMessage ??
      this.mergedConfig.customErrorMessage ??
      'Invalid file type or size'
    );
  }

  get acceptAttribute(): string {
    return this.mergedConfig.acceptAttribute || '*';
  }
}
